import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getSDEEvent, getRegistration, upsertRegistration } from '../../Components/SDE24/sdeSupabaseService';
import { sendSDOAutoConfirmationEmail } from '../../../api';
import { POPULATION_TYPES, STEPS_POP_A, STEPS_POP_B, INITIAL_POP_A_DATA, INITIAL_POP_B_DATA } from '../../Components/SDE24/sdeConstants';

// Step components
import PopAStep1_Representative from '../../Components/SDE24/Steps/PopAStep1_Representative';
import PopAStep2_Needs from '../../Components/SDE24/Steps/PopAStep2_Needs';
import PopAStep3_Projects from '../../Components/SDE24/Steps/PopAStep3_Projects';
import PopBStep2_Expertise from '../../Components/SDE24/Steps/PopBStep2_Expertise';
import PopBStep3_Portfolio from '../../Components/SDE24/Steps/PopBStep3_Portfolio';
import SharedStep_Preferences from '../../Components/SDE24/Steps/SharedStep_Preferences';
import SharedStep_Review from '../../Components/SDE24/Steps/SharedStep_Review';

// Styled Components
const PageContainer = styled.div`
  max-width: 900px; margin: 0 auto; padding: 2rem 1.5rem 4rem;
  min-height: 80vh;
`;

const EventHeader = styled.div`
  text-align: center; margin-bottom: 2rem;
  h1 { font-size: 1.75rem; font-weight: 700; color: #1F2937; margin-bottom: 0.25rem; }
  p { color: #6B7280; font-size: 0.95rem; }
`;

const PopBadge = styled.span`
  display: inline-block; padding: 0.3rem 1rem; border-radius: 999px; font-size: 0.85rem; font-weight: 600;
  background: ${props => props.type === 'pop_a' ? '#DBEAFE' : '#D1FAE5'};
  color: ${props => props.type === 'pop_a' ? '#1E40AF' : '#065F46'};
  margin-bottom: 1rem;
`;

// Progress bar
const ProgressContainer = styled.div`
  background: white; border-radius: 12px; padding: 1.25rem 1.5rem; margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const ProgressSteps = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;

const StepDot = styled.div`
  display: flex; flex-direction: column; align-items: center; flex: 1; position: relative;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
`;

const Dot = styled.div`
  width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 600; transition: all 0.3s;
  background: ${props => props.active ? '#2563EB' : props.completed ? '#10B981' : '#E5E7EB'};
  color: ${props => (props.active || props.completed) ? 'white' : '#9CA3AF'};
`;

const StepLabel = styled.div`
  margin-top: 0.4rem; font-size: 0.75rem; font-weight: 500; text-align: center;
  color: ${props => props.active ? '#2563EB' : '#9CA3AF'};
`;

const ProgressBar = styled.div`
  height: 4px; background: #E5E7EB; border-radius: 2px; margin-top: 0.75rem;
  position: relative;
  &::after {
    content: ''; position: absolute; left: 0; top: 0; height: 100%;
    width: ${props => props.progress}%;
    background: linear-gradient(90deg, #2563EB, #10B981);
    border-radius: 2px; transition: width 0.3s;
  }
`;

// Navigation
const NavBar = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid #E5E7EB;
`;

const NavButton = styled.button`
  padding: 0.75rem 2rem; border-radius: 10px; font-size: 0.95rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  background: ${props => props.primary ? '#2563EB' : 'white'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: ${props => props.primary ? 'none' : '2px solid #E5E7EB'};
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;

const StepCounter = styled.span`
  font-size: 0.85rem; color: #9CA3AF; font-weight: 500;
`;

// Save indicator
const SaveIndicator = styled.div`
  position: fixed; bottom: 20px; right: 20px; padding: 0.6rem 1.2rem; border-radius: 8px;
  font-size: 0.85rem; font-weight: 500; z-index: 100; transition: all 0.3s;
  background: ${props => props.status === 'saving' ? '#3B82F6' : props.status === 'saved' ? '#10B981' : props.status === 'error' ? '#EF4444' : '#6B7280'};
  color: white; opacity: ${props => props.visible ? 1 : 0}; transform: translateY(${props => props.visible ? 0 : '10px'});
`;

// Test toggle
const TestToggleBar = styled.div`
  background: #FEF3C7; border: 1px solid #FCD34D; border-radius: 8px; padding: 0.75rem 1rem;
  display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.5rem; flex-wrap: wrap;
  span { color: #92400E; font-size: 0.85rem; font-weight: 600; }
`;

const ToggleBtn = styled.button`
  padding: 0.4rem 0.75rem; border-radius: 6px; font-size: 0.8rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s; border: none;
  background: ${props => props.active ? '#2563EB' : '#FDE68A'};
  color: ${props => props.active ? 'white' : '#92400E'};
`;

const AdminBar = styled.div`
  background: #FFEDD5; border: 1px solid #FB923C; border-radius: 8px; padding: 0.75rem 1rem;
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 1.5rem; flex-wrap: wrap;
  color: #9A3412; font-size: 0.9rem; font-weight: 600;
  button { background: white; border: 1px solid #FB923C; color: #9A3412; border-radius: 6px; padding: 0.35rem 0.75rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
`;

const Loading = styled.div`text-align: center; padding: 4rem; color: #9CA3AF; font-size: 1.1rem;`;
const ErrorMessage = styled.div`
  background: #FEE2E2; border: 1px solid #FECACA; border-radius: 8px; padding: 1rem;
  color: #DC2626; text-align: center; margin: 2rem 0;
`;

const DEV_MODE = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_SDE_TEST_TOGGLE === 'true';

const RegisterSDE = ({ fullUser, fullUserLoading }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation('common');
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const eventId = searchParams.get('eid');
  const urlPop = searchParams.get('pop') === 'a' ? POPULATION_TYPES.POP_A : POPULATION_TYPES.POP_B;
  // Admin "act as" mode: fill the form on behalf of another email
  const actAs = (fullUser?.user?.role === 'admin' && searchParams.get('as')) ? searchParams.get('as').trim() : null;
  const targetEmail = actAs || fullUser?.user?.email;

  const [testPopulation, setTestPopulation] = useState(null);
  const populationType = (DEV_MODE && testPopulation) ? testPopulation : urlPop;

  const [event, setEvent] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(populationType === POPULATION_TYPES.POP_A ? { ...INITIAL_POP_A_DATA } : { ...INITIAL_POP_B_DATA });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveVisible, setSaveVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loadedStatus, setLoadedStatus] = useState('draft');

  const isPoplA = populationType === POPULATION_TYPES.POP_A;
  const steps = isPoplA ? STEPS_POP_A : STEPS_POP_B;

  // Load event and existing registration
  useEffect(() => {
    async function load() {
      if (!eventId) { setError('No event ID provided'); setLoading(false); return; }

      setLoading(true);
      try {
        const { data: eventData, error: eventError } = await getSDEEvent(eventId);
        if (eventError) throw eventError;
        setEvent(eventData);

        // Load existing registration if user is logged in
        if (targetEmail) {
          const { data: reg } = await getRegistration(eventId, targetEmail, populationType);
          if (reg) {
            // Merge existing reg data into form
            const existing = { ...formData };
            Object.keys(reg).forEach(key => {
              if (reg[key] !== null && reg[key] !== undefined && !['id', 'event_id', 'user_email', 'mongo_user_id', 'population_type', 'status', 'created_at', 'updated_at'].includes(key)) {
                existing[key] = reg[key];
              }
            });
            setFormData(existing);
            if (reg.status) setLoadedStatus(reg.status);
          }
        }
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load event');
      }
      setLoading(false);
    }
    load();
  }, [eventId, targetEmail, populationType]);

  // Reset form when switching population type
  useEffect(() => {
    setFormData(isPoplA ? { ...INITIAL_POP_A_DATA } : { ...INITIAL_POP_B_DATA });
    setCurrentStep(0);
    setIsSubmitted(false);
  }, [populationType]);

  // Auto-fill user info
  useEffect(() => {
    if (actAs) {
      if (formData.representative_1?.email === '') {
        updateData({ representative_1: { ...formData.representative_1, email: actAs } });
      }
      return;
    }
    if (fullUser?.user && formData.representative_1?.email === '') {
      updateData({
        representative_1: {
          ...formData.representative_1,
          email: fullUser.user.email || '',
          name: fullUser.user.nom || '',
          firstname: fullUser.user.prenom || '',
          phone: fullUser.user.tel || '',
        },
        company_name: fullUser.company?.nom || formData.company_name || '',
      });
    }
  }, [fullUser]);

  const updateData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Auto-save draft
  useEffect(() => {
    if (loading || !eventId) return;
    const timeout = setTimeout(async () => {
      if (!targetEmail) return;

      setSaveStatus('saving');
      setSaveVisible(true);
      try {
        await upsertRegistration({
          event_id: eventId,
          user_email: targetEmail,
          ...(actAs ? {} : { mongo_user_id: fullUser.user._id }),
          population_type: populationType,
          status: loadedStatus || 'draft',
          ...formData,
        });
        setSaveStatus('saved');
        setTimeout(() => setSaveVisible(false), 2000);
      } catch (err) {
        console.error('Auto-save error:', err);
        setSaveStatus('error');
        setTimeout(() => setSaveVisible(false), 3000);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [formData, eventId, populationType]);

  const handleSubmit = async () => {
    const email = targetEmail;
    if (!email) { setError('You must be logged in to submit'); return; }

    setIsSubmitting(true);
    try {
      await upsertRegistration({
        event_id: eventId,
        user_email: email,
        ...(actAs ? {} : { mongo_user_id: fullUser.user._id }),
        population_type: populationType,
        status: loadedStatus === 'validated' ? 'validated' : 'submitted',
        ...formData,
      });
      setIsSubmitted(true);
      setIsSubmitting(false);
      setLoadedStatus(loadedStatus === 'validated' ? 'validated' : 'submitted');

      // Send auto-confirmation email (fire-and-forget, don't block UI); skipped when an admin submits on behalf of someone
      if (actAs) return;
      try {
        const repName = formData.representative_1?.firstname || formData.representative_1?.first_name || fullUser.user.prenom || '';
        sendSDOAutoConfirmationEmail({ recipientEmail: email, recipientName: repName, populationType }).catch(emailErr => {
          console.error('Auto-confirmation email error:', emailErr);
        });
      } catch (emailErr) {
        console.error('Auto-confirmation email error:', emailErr);
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to submit');
      setIsSubmitting(false);
    }
  };

  const goToStep = (step) => {
    if (step >= 0 && step < steps.length) setCurrentStep(step);
  };

  const renderStep = () => {
    if (isPoplA) {
      switch (currentStep) {
        case 0: return <PopAStep1_Representative data={formData} updateData={updateData} lang={lang} />;
        case 1: return <PopAStep2_Needs data={formData} updateData={updateData} lang={lang} />;
        case 2: return <PopAStep3_Projects data={formData} updateData={updateData} lang={lang} eventId={eventId} />;
        case 3: return <SharedStep_Preferences data={formData} updateData={updateData} lang={lang} />;
        case 4: return <SharedStep_Review data={formData} populationType={populationType} onSubmit={handleSubmit} isSubmitting={isSubmitting} isSubmitted={isSubmitted} loadedStatus={loadedStatus} lang={lang} eventId={eventId} />;
        default: return null;
      }
    } else {
      switch (currentStep) {
        case 0: return <PopAStep1_Representative data={formData} updateData={updateData} lang={lang} populationType={populationType} />;
        case 1: return <PopBStep2_Expertise data={formData} updateData={updateData} lang={lang} />;
        case 2: return <PopBStep3_Portfolio data={formData} updateData={updateData} lang={lang} eventId={eventId} />;
        case 3: return <SharedStep_Preferences data={formData} updateData={updateData} lang={lang} populationType={populationType} />;
        case 4: return <SharedStep_Review data={formData} populationType={populationType} onSubmit={handleSubmit} isSubmitting={isSubmitting} isSubmitted={isSubmitted} loadedStatus={loadedStatus} lang={lang} eventId={eventId} />;
        default: return null;
      }
    }
  };

  if (loading || fullUserLoading) return <PageContainer><Loading>{lang === 'fr' ? 'Chargement...' : 'Loading...'}</Loading></PageContainer>;
  if (error) return <PageContainer><ErrorMessage>{error}</ErrorMessage></PageContainer>;
  if (!event) return <PageContainer><ErrorMessage>Event not found</ErrorMessage></PageContainer>;

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <PageContainer>
      {actAs && (
        <AdminBar>
          <span>Mode admin — vous remplissez ce formulaire pour {actAs}</span>
          <button onClick={() => navigate(`/admin/events/sdo?eid=${eventId}`)}>← Retour admin</button>
        </AdminBar>
      )}

      {/* Test Toggle (dev only) */}
      {DEV_MODE && (
        <TestToggleBar>
          <span>Test Mode:</span>
          <ToggleBtn active={populationType === POPULATION_TYPES.POP_A} onClick={() => setTestPopulation(POPULATION_TYPES.POP_A)}>
            Pop A (Entreprise)
          </ToggleBtn>
          <ToggleBtn active={populationType === POPULATION_TYPES.POP_B} onClick={() => setTestPopulation(POPULATION_TYPES.POP_B)}>
            Pop B (Designer)
          </ToggleBtn>
          {testPopulation && (
            <ToggleBtn onClick={() => setTestPopulation(null)}>
              Utiliser URL ({urlPop === POPULATION_TYPES.POP_A ? 'A' : 'B'})
            </ToggleBtn>
          )}
        </TestToggleBar>
      )}

      {/* Event Header */}
      <EventHeader>
        <PopBadge type={populationType}>
          {isPoplA
            ? (lang === 'fr' ? 'Entreprise (Pop A)' : 'Company (Pop A)')
            : (lang === 'fr' ? 'Designer (Pop B)' : 'Designer (Pop B)')
          }
        </PopBadge>
        <h1>{lang === 'fr' ? event.title_fr : event.title_en || event.title_fr}</h1>
        <p>{isPoplA
          ? (lang === 'fr' ? 'Exprimez vos besoins' : 'Express your needs')
          : (lang === 'fr' ? 'Présentez votre expertise' : 'Present your expertise')
        }</p>
      </EventHeader>

      {/* Progress */}
      <ProgressContainer>
        <ProgressSteps>
          {steps.map((step, i) => (
            <StepDot key={step.key} clickable={i < currentStep} onClick={() => i < currentStep && goToStep(i)}>
              <Dot active={i === currentStep} completed={i < currentStep}>
                {i < currentStep ? '✓' : i + 1}
              </Dot>
              <StepLabel active={i === currentStep}>
                {lang === 'fr' ? step.label_fr : step.label_en}
              </StepLabel>
            </StepDot>
          ))}
        </ProgressSteps>
        <ProgressBar progress={progress} />
      </ProgressContainer>

      {/* Current Step */}
      {renderStep()}

      {/* Navigation (hide on review step when submitted) */}
      {!(currentStep === steps.length - 1 && isSubmitted) && (
        <NavBar>
          <NavButton disabled={currentStep === 0} onClick={() => goToStep(currentStep - 1)}>
            {lang === 'fr' ? 'Précédent' : 'Previous'}
          </NavButton>
          <StepCounter>
            {lang === 'fr' ? `Étape ${currentStep + 1} sur ${steps.length}` : `Step ${currentStep + 1} of ${steps.length}`}
          </StepCounter>
          {currentStep < steps.length - 1 && (
            <NavButton primary onClick={() => goToStep(currentStep + 1)}>
              {lang === 'fr' ? 'Suivant' : 'Next Step'}
            </NavButton>
          )}
          {currentStep === steps.length - 1 && <div />}
        </NavBar>
      )}

      {/* Auto-save indicator */}
      <SaveIndicator status={saveStatus} visible={saveVisible}>
        {saveStatus === 'saving' && (lang === 'fr' ? 'Sauvegarde...' : 'Saving...')}
        {saveStatus === 'saved' && (lang === 'fr' ? 'Sauvegardé' : 'Saved')}
        {saveStatus === 'error' && (lang === 'fr' ? 'Erreur de sauvegarde' : 'Save error')}
      </SaveIndicator>
    </PageContainer>
  );
};

export default RegisterSDE;
